import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import QRCode from "qrcode";
import { website } from "../server";

// Classes for which students choose individual subjects — for these,
// the admit card should only list exams for subjects the student actually
// has in their "subjects" array, not every exam configured for the class.
const SUBJECT_FILTERED_CLASSES = ["IX", "X", "XI", "XII"];

// Classes where students also belong to a stream (Science/Commerce/Humanities).
// For these, exam rows tagged with a stream should only show up for
// students in that stream.
const STREAM_FILTERED_CLASSES = ["XI", "XII"];

const shouldFilterBySubjects = (studentDetails) => {
  const cls = studentDetails?.currentClass;
  if (!cls) return false;
  return SUBJECT_FILTERED_CLASSES.includes(cls.toString().trim().toUpperCase());
};

const shouldFilterByStream = (studentDetails) => {
  const cls = studentDetails?.currentClass;
  if (!cls) return false;
  return STREAM_FILTERED_CLASSES.includes(cls.toString().trim().toUpperCase());
};

// Keeps only the exam rows that match the student's stream.
// An exam row is considered stream-specific if it has a "stream" field
// (string) or a "streams" field (array). Rows with no stream info at all
// are treated as common to every stream and always kept.
const filterExamsByStream = (exams, studentObj, applyFilter) => {
  if (!Array.isArray(exams)) return exams;
  if (!applyFilter) return exams;

  const studentStream = studentObj?.studentDetails?.stream;

  if (!studentStream) {
    console.warn(
      `[admit-card] No "stream" set on studentDetails for admission_no ${
        studentObj?.studentDetails?.admission_no
      } (class ${
        studentObj?.studentDetails?.currentClass
      }) — stream filtering skipped for this student.`,
    );
    return exams;
  }

  return exams.filter((exam) => {
    if (Array.isArray(exam.streams) && exam.streams.length > 0) {
      return exam.streams.includes(studentStream);
    }
    if (exam.stream) {
      return exam.stream === studentStream;
    }
    // No stream tag on this exam row — treat as applicable to all streams.
    return true;
  });
};

// Keeps only the exam rows whose subject(s) the student has selected.
// For a multi-subject exam row (exam.subject as an array), only the
// subjects the student has are kept; if none remain, the row is dropped.
const filterExamsForStudent = (exams, studentObj, applyFilter) => {
  if (!Array.isArray(exams)) return exams;
  if (!applyFilter) return exams;

  const studentSubjects = Array.isArray(studentObj?.subjects)
    ? studentObj.subjects
    : [];

  // If the student has no subjects recorded, don't filter anything out —
  // fall back to showing the full exam list rather than an empty card.
  if (studentSubjects.length === 0) {
    console.warn(
      `[admit-card] No "subjects" array on studentObj for admission_no ${
        studentObj?.studentDetails?.admission_no
      } (class ${
        studentObj?.studentDetails?.currentClass
      }) — subject filtering skipped for this student.`,
    );
    return exams;
  }

  return exams
    .map((exam) => {
      const subjectArray = Array.isArray(exam.subject)
        ? exam.subject
        : [exam.subject];
      const keptSubjects = subjectArray.filter((subj) =>
        studentSubjects.includes(subj),
      );
      if (keptSubjects.length === 0) return null;
      return {
        ...exam,
        subject: Array.isArray(exam.subject) ? keptSubjects : keptSubjects[0],
      };
    })
    .filter(Boolean);
};

// Runs stream filtering first, then subject filtering, on an exam list.
const filterExamsForStudentFull = (exams, studentObj) => {
  const applySubjectFilter = shouldFilterBySubjects(studentObj?.studentDetails);
  const applyStreamFilter = shouldFilterByStream(studentObj?.studentDetails);

  const streamFiltered = filterExamsByStream(
    exams,
    studentObj,
    applyStreamFilter,
  );
  return filterExamsForStudent(streamFiltered, studentObj, applySubjectFilter);
};

// Resolves the class teacher's staff ID for a given student, from
// whatever shape GetUserDetailByClassTeacherAndSection returned:
// - a single teacher object (one class/section was requested), or
// - a map keyed by "class|||section" (used when several sections' worth
//   of teachers were fetched at once).
const getClassTeacherInfo = (teacherData, student) => {
  if (!teacherData) return { name: "", staffId: "" };

  const isMap =
    teacherData && !teacherData.basicDetails && !teacherData.staffId;
  const teacher = isMap
    ? teacherData[`${student.currentClass}|||${student.currentSection}`]
    : teacherData;

  return {
    name: teacher?.basicDetails?.fullName || "",
    staffId: teacher?.staffId || "",
  };
};

const DownloadAdmitCard = async (
  sortedStudents,
  admitCardDetail,
  teacherData,
) => {
  if (!sortedStudents || !admitCardDetail) {
    toast.error("Missing Details", { position: "top-right" });
    return;
  }

  const examTimings = [
    { key: "oral", label: "Oral examination", timeKey: "oralExaminationTime" },
    {
      key: "dictation",
      label: "Dictation examination",
      timeKey: "dictationExaminationTime",
    },
    {
      key: "written",
      label: "Written examination",
      timeKey: "writtenExaminationTime",
    },
  ];

  const available = examTimings.filter((exam) => {
    const hasExams =
      Array.isArray(admitCardDetail[exam.key]) &&
      admitCardDetail[exam.key].length > 0;
    const time = admitCardDetail[exam.timeKey];
    const hasTime = typeof time === "string" && time.trim() !== "";
    return hasExams && hasTime;
  });

  const sentences = available.map((exam, idx) => {
    const time = admitCardDetail[exam.timeKey].trim();
    if (idx === 0) {
      return `${exam.label} will be conducted from <b>${time}</b>`;
    }
    return `${exam.label}<br />will be conducted from <b>${time}</b>`;
  });

  const noteText = sentences.length ? sentences.join(". ") : "";

  const qrCodes = await Promise.all(
    sortedStudents.map(async (studentObj) => {
      const student = studentObj.studentDetails;
      const studentUrl = `${website}student/${student.studentId}/${student.admission_no}/${student.aadhar_no}`;
      return QRCode.toDataURL(studentUrl, { width: 100 });
    }),
  );

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const pdf = new jsPDF("p", "mm", "a4");

  for (let i = 0; i < sortedStudents.length; i += 2) {
    const studentsPair = sortedStudents.slice(i, i + 2);

    const pageContainer = document.createElement("div");
    pageContainer.style.position = "fixed";
    pageContainer.style.top = "-10000px";
    pageContainer.style.left = "-10000px";
    pageContainer.style.width = "210mm";
    pageContainer.style.height = "297mm";
    pageContainer.style.backgroundColor = "#fff";
    pageContainer.style.display = "flex";
    pageContainer.style.flexDirection = "column";
    pageContainer.style.justifyContent = "space-between";

    studentsPair.forEach((studentObj, idx) => {
      const student = studentObj.studentDetails;
      const parents = studentObj.parentDetails || [];
      const father = parents.find((p) => p.relation === "Father")?.name || "";
      const mother = parents.find((p) => p.relation === "Mother")?.name || "";
      const qrDataUrl = qrCodes[i + idx];

      const { staffId: classTeacherStaffId } = getClassTeacherInfo(
        teacherData,
        student,
      );

      const oralExams = filterExamsForStudentFull(
        admitCardDetail.oral,
        studentObj,
      );
      const dictationExams = filterExamsForStudentFull(
        admitCardDetail.dictation,
        studentObj,
      );
      const writtenExams = filterExamsForStudentFull(
        admitCardDetail.written,
        studentObj,
      );

      const renderExamRows = (title, exams, color) => {
        if (!exams || exams.length === 0) return "";
        return `
          <tr>
            <td colspan="4" class="p-0 fw-bold text-${color}">${title}</td>
          </tr>
          ${exams
            .map(
              (exam, idx) => `
            <tr>
              <td class="p-0 text-black">${idx + 1}.</td>
              <td class="p-0 text-black">${formatDate(exam.date)}</td>
              <td class="p-0 text-black text-uppercase">${exam.day}</td>
              <td class="p-0 text-black">${
                Array.isArray(exam.subject)
                  ? exam.subject.join(" + ")
                  : exam.subject
              }</td>
            </tr>
          `,
            )
            .join("")}
        `;
      };

      const examTableRows = `
        ${renderExamRows("ORAL EXAMINATION", oralExams, "success")}
        ${renderExamRows("DICTATION EXAMINATION", dictationExams, "success")}
        ${renderExamRows("WRITTEN EXAMINATION", writtenExams, "danger")}
      `;

      const admitCardBlock = document.createElement("div");
      admitCardBlock.style.flex = "1";
      admitCardBlock.style.padding = "10px 0";

      admitCardBlock.innerHTML = `
        <div class="mx-3 my-1">
          <div class="bg-watermark-logo">
            <div class="bg-primary rounded-4 px-3 py-1 mb-1">
              <div class="row align-items-center my-1">
                <div class="col-3">
                  <img src="/assets/images/icon.png" width="65" height="65"/>
                </div>
                <div class="col-6">
                  <h2 class="eczar-font text-center text-white lh-sm m-0">आचार्यकुलम्</h2>
                  <p class="fs-4 text-white lh-sm m-0 text-center">Tata Road, Namkum, Ranchi - 834010</p>
                  <h3 class="text-white fs-4 lh-sm mb-0 fw-bold text-center text-uppercase">
                    ${admitCardDetail.term} ADMIT CARD
                  </h3>
                </div>
                <div class="col-3 ps-5">
                  <h5 class="m-0 lh-1 text-white text-center">Session</h5>
                  <h3 class="text-white fw-bold lh-sm text-center">
                    ${admitCardDetail.session}
                  </h3>
                </div>
              </div>
            </div>

            <div class="px-3 pt-2 row">
              <div class="col-5">
                <p class="m-0 fs-5 text-dark lh-sm">
                  Student's Name : <span class="fw-bold">${student.name}</span>
                </p>
                <p class="m-0 fs-5 text-dark lh-sm">
                  Father's Name : <span class="fw-bold">${father}</span>
                </p>
                <p class="m-0 fs-5 text-dark lh-sm">
                  Mother's Name : <span class="fw-bold">${mother}</span>
                </p>
              </div>

              <div class="col-4">
                <p class="m-0 fs-5 text-dark lh-sm">
                  Admission No : <span class="fw-bold">${
                    student.admission_no
                  }</span>
                </p>
                <p class="m-0 fs-5 text-dark lh-sm">
                  Class / Section : 
                  <span class="fw-bold">
                    ${student.currentClass} - ${student.currentSection}${
                      student.stream ? ` (${student.stream})` : ""
                    }
                  </span>
                </p>
                <p class="m-0 fs-5 text-dark lh-sm">
                  Roll No : <span class="fw-bold">${student.rollNo}</span>
                </p>
              </div>

              <div class="col-3">
                <div class="d-flex justify-content-end">
                  <div>
                    <div class="bg-light p-1 rounded-4 position-relative z-1 me-n2 d-flex">
                      <img src="${qrDataUrl}" class="rounded-4" width="55" height="55" />
                    </div>
                  </div>

                  <img 
                    src="${student.studentImage}"
                    crossorigin="anonymous"
                    class="rounded-4 mb-2 object-fit-cover"
                    width="90"
                    height="90"
                  />
                </div>
              </div>

              <p class="m-0 fs-5 text-danger lh-1 mt-n6">
                Note: ${noteText}
              </p>
            </div>

            <div class="px-3 mb-2">
              <table class="table border-dark rounded-table mb-2 text-center">
                <thead>
                  <tr>
                    <th class="p-0 text-black fw-bold">S. No</th>
                    <th class="p-0 text-black fw-bold">Date</th>
                    <th class="p-0 text-black fw-bold">Day</th>
                    <th class="p-0 text-black fw-bold">Subject</th>
                  </tr>
                </thead>
                <tbody>
                  ${examTableRows}
                </tbody>
              </table>
            </div>
            
            <div class="bg-warning rounded-4 px-3 pb-2 pt-2">
              <div class="d-flex align-items-end justify-content-between">
                <div class="ms-7">
                  <img src="/assets/images/teachers/${classTeacherStaffId}.png" width="140" class="mb-n3 ms-n3" />
                  <h5 class="m-0 lh-1 text-black">Class Teacher</h5>
                </div>
                <div class="me-7">
                  <img src="/assets/images/principal.png" width="65" class="mb-n1" />
                  <h5 class="m-0 lh-1 text-black">Principal</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      pageContainer.appendChild(admitCardBlock);
    });

    document.body.appendChild(pageContainer);

    const canvas = await html2canvas(pageContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#fff",
    });

    const imgData = canvas.toDataURL("image/png");

    if (i > 0) pdf.addPage();
    pdf.addImage(imgData, "PNG", 0, 0, 210, 297);

    pageContainer.remove();
  }

  const blobUrl = pdf.output("bloburl");
  window.open(blobUrl, "_blank");
};

export default DownloadAdmitCard;
